import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import axios from 'axios'
import { PublicKanbanColumn } from './PublicKanbanColumn'
import './PublicClient.css'

interface Task {
  id: number
  name: string
  priority: string
  status: string
  stage_id?: number | null
  project_name: string
  total_time: number
  users: Array<{
    name: string
    avatar: string
  }>
  hasTag: boolean
  hasChecklist: boolean
  hasDeadline: boolean
  expected_date?: string
  isOverdue?: boolean
  color?: string
}

interface Stage {
  id: number
  name: string
  base_stage?: {
    name: string
  }
}

interface Column {
  id: string
  title: string
  color: string
  tasks: Task[]
}

type Props = {
  clientUrl: string
  isAuthenticated?: boolean
  token?: string | null
}

const colorStatusMap: {[key: string]: string} = {
  backlog: 'backlog',
  'em andamento': 'in-progress',
  entregue: 'done',
}

const PublicKanbanBoard: React.FC<Props> = ({ clientUrl, isAuthenticated = false, token = null }) => {
  const intl = useIntl()
  const [columns, setColumns] = useState<Column[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchData()
  }, [clientUrl])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const apiUrl = process.env.REACT_APP_API_URL

      // Configure headers if authenticated
      const headers: Record<string, string> = {}
      if (isAuthenticated && token) {
        headers['Authorization'] = `Bearer ${token}`
        headers['Content-Type'] = 'application/json'
      }

      // Use protected endpoints if authenticated, otherwise use public endpoints
      const baseUrl = isAuthenticated 
        ? `${apiUrl}/api/client-portal/project/${clientUrl}`
        : `${apiUrl}/api/public/client/${clientUrl}`

      // Buscar stages e tarefas em paralelo
      const [stagesResponse, tasksResponse] = await Promise.all([
        axios.get(`${baseUrl}/stages`, { headers }),
        axios.get(`${baseUrl}/tasks`, { headers })
      ])

      // Handle different response formats
      const stages: Stage[] = stagesResponse.data?.data || stagesResponse.data || []
      const tasks: Task[] = tasksResponse.data?.data || tasksResponse.data || []

      // Organizar tarefas por stage
      const tasksByStage = organizeTasksByStage(stages, tasks)
      setColumns(tasksByStage)
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error)
      setError(error.response?.data?.message || 'Erro ao carregar tarefas')
    } finally {
      setLoading(false)
    }
  }

  const organizeTasksByStage = (stages: Stage[], tasks: Task[]): Column[] => {
    // Se não houver stages personalizadas, usar as colunas padrão baseadas em status
    if (!stages || stages.length === 0) {
      return organizeTasksByStatus(tasks)
    }

    // Criar um mapa de tarefas por stage_id
    const tasksByStageId: { [key: number]: Task[] } = {}
    
    tasks.forEach(task => {
      if (task.stage_id) {
        if (!tasksByStageId[task.stage_id]) {
          tasksByStageId[task.stage_id] = []
        }
        tasksByStageId[task.stage_id].push(task)
      }
    })

    // Criar colunas baseadas nas stages, mantendo a ordem
    const columns: Column[] = stages.map(stage => {
      const stageTasks = tasksByStageId[stage.id] || []
      const baseStageName = stage.base_stage?.name?.toLowerCase() ?? stage.name?.toLowerCase() ?? ''
      const color = colorStatusMap[baseStageName] || 'custom-gray'

      return {
        id: String(stage.id),
        title: stage.name || 'Sem nome',
        color: color,
        tasks: stageTasks
      }
    })

    return columns
  }

  const organizeTasksByStatus = (tasks: Task[]): Column[] => {
    const statusMap: { [key: string]: Task[] } = {}
    
    tasks.forEach(task => {
      // Normalizar status para minúsculo e mapear variações
      let normalizedStatus = task.status.toLowerCase().trim()
      
      // Mapear variações de status
      if (normalizedStatus === 'em andamento' || normalizedStatus === 'em andamento') {
        normalizedStatus = 'em andamento'
      } else if (normalizedStatus === 'entregue') {
        normalizedStatus = 'entregue'
      } else if (normalizedStatus === 'backlog') {
        normalizedStatus = 'backlog'
      }
      
      if (!statusMap[normalizedStatus]) {
        statusMap[normalizedStatus] = []
      }
      statusMap[normalizedStatus].push(task)
    })

    // Definir colunas padrão
    const defaultColumns: Column[] = [
      {
        id: 'backlog',
        title: 'Backlog',
        color: 'backlog',
        tasks: statusMap['backlog'] || []
      },
      {
        id: 'em andamento',
        title: 'Em Andamento',
        color: 'in-progress',
        tasks: statusMap['em andamento'] || []
      },
      {
        id: 'entregue',
        title: 'Entregue',
        color: 'done',
        tasks: statusMap['entregue'] || []
      }
    ]

    return defaultColumns
  }

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    
    if (hours > 0) {
      return `${hours}h ${mins}m`
    }
    return `${mins}m`
  }

  const totalTasks = columns.reduce((total, col) => total + col.tasks.length, 0)

  if (loading) {
    return (
      <div className='pc-state'>
        <div className='spinner-border pc-spinner' role='status'>
          <span className='visually-hidden'>Carregando...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='pc-state' role='alert'>
        <i className='bi bi-exclamation-triangle' aria-hidden='true'></i>
        <p className='pc-state-title'>Não foi possível carregar as tarefas</p>
        <p className='mb-0'>{error}</p>
      </div>
    )
  }

  return (
    <section className='pc-section'>
      <div className='pc-section-header'>
        <h2 className='pc-section-title'>Tarefas do projeto</h2>
        <span className='pc-section-meta'>
          {totalTasks} {totalTasks === 1 ? 'tarefa' : 'tarefas'}
        </span>
      </div>

      {totalTasks === 0 ? (
        <div className='pc-state'>
          <i className='bi bi-inbox' aria-hidden='true'></i>
          <p className='pc-state-title'>Nenhuma tarefa por aqui ainda</p>
          <p className='mb-0'>As tarefas do projeto aparecem nesta tela conforme forem criadas.</p>
        </div>
      ) : (
        <div className='pc-kanban'>
          {columns.map((column) => (
            <PublicKanbanColumn
              key={column.id}
              id={column.id}
              title={column.title}
              color={column.color}
              tasks={column.tasks}
              formatTime={formatTime}
              clientUrl={clientUrl}
              isAuthenticated={isAuthenticated}
              token={token}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export { PublicKanbanBoard }
